import React, { FormEvent, useState, useEffect } from 'react';
import { Head, useForm } from '@inertiajs/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card } from '../../components/ui/card';
import PlacesAutocomplete from '../../components/PlacesAutocomplete';

interface FormData {
  name: string;
  email: string;
  user_intent: string;
  birth_date: string;
  birth_time: string;
  birth_city: string;
  birth_country: string;
  latitude: string;
  longitude: string;
}

interface InviteFormProps {
  token: string;
  inviteName: string;
  errors?: Record<string, string>;
  formData?: Partial<FormData>;
}

const InviteForm: React.FC<InviteFormProps> = ({
  token,
  inviteName,
  errors = {},
  formData = {},
}) => {
  const [emailError, setEmailError] = useState<string>('');
  
  const initialData: FormData = {
    name: formData.name || '',
    email: formData.email || '',
    user_intent: formData.user_intent || '',
    birth_date: formData.birth_date || '',
    birth_time: formData.birth_time || '',
    birth_city: formData.birth_city || '',
    birth_country: formData.birth_country || '',
    latitude: formData.latitude || '',
    longitude: formData.longitude || '',
  };

  const { data, setData, post, processing } = useForm({
    bodygraph: initialData,
  });

  // Email validation regex pattern
  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

  const validateEmail = (email: string): boolean => {
    return emailRegex.test(email);
  };

  // Load Google Maps API script with proper callback
  // NOTE: Using callback parameter is required for async/defer loading
  useEffect(() => {
    console.group('🔍 INVITEFORM GOOGLE MAPS LOADING');
    console.log('🔍 Checking existing Google Maps state...');
    
    // Check if already loaded
    console.log('🔍 google defined:', typeof google !== 'undefined');
    console.log('🔍 google.maps defined:', !!(google as any)?.maps);
    console.log('🔍 google.maps.places defined:', !!(google as any)?.maps?.places);
    
    if (typeof google !== 'undefined' && google.maps && google.maps.places) {
      console.log('🔍 Google Maps already loaded - skipping');
      console.groupEnd();
      return;
    }

    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      console.warn('🔍 Google Maps API key not configured');
      console.groupEnd();
      return;
    }

    const scriptId = 'google-maps-script';
    if (document.getElementById(scriptId)) {
      console.log('🔍 Script tag already exists - skipping');
      console.groupEnd();
      return;
    }

    console.log('🔍 Loading Google Maps with callback...');
    
    // Define global callback before loading script
    (window as any).googleMapsApiLoaded = () => {
      console.log('🔍 Google Maps API callback fired');
      console.log('🔍 google.maps available:', !!(google as any)?.maps?.places);
    };
    
    const script = document.createElement('script');
    script.id = scriptId;
    // FIXED: Added callback parameter - this is required for async/defer loading
    // Without callback, the API fails with 'Cannot read properties of undefined (reading Up)'
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=googleMapsApiLoaded`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      console.log('🔍 Google Maps API script loaded successfully');
    };
    
    script.onerror = () => {
      console.error('🔍 Failed to load Google Maps API script');
    };
    
    document.head.appendChild(script);
    console.groupEnd();
  }, []);

  const handlePlaceSelect = (place: {
    name: string;
    latitude: number;
    longitude: number;
    country?: string;
  }) => {
    setData('bodygraph.birth_city', place.name);
    setData('bodygraph.latitude', place.latitude.toString());
    setData('bodygraph.longitude', place.longitude.toString());
    if (place.country) {
      setData('bodygraph.birth_country', place.country);
    }
  };

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    
    // Client-side email validation
    if (!validateEmail(data.bodygraph.email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError('');
    
    post(`/public/invites/${token}`);
  }

  return (
    <div className="min-h-screen w-full bg-background flex items-center justify-center p-4 md:p-6 lg:p-8">
      <Head title="Complete Your Profile">
        <meta property="og:title" content="Complete Your Profile" />
        <meta property="og:description" content="Click to complete your profile" />
        <meta property="og:image" content="https://hd.supavp.com/og-image.svg" />
        <meta property="og:url" content={`https://hd.supavp.com/public/invites/${token}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Complete Your Profile" />
        <meta name="twitter:description" content="Click to complete your profile" />
        <meta name="twitter:image" content="https://hd.supavp.com/og-image.svg" />
      </Head>

      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
            Complete Your Profile
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            {inviteName ? `Invitation for ${inviteName}` : 'Enter your details to complete your profile'}
          </p>
        </div>

        {/* Form Card */}
        <Card className="p-5 sm:p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Global Errors */}
            {Object.keys(errors).length > 0 && (
              <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-md">
                <p className="font-medium">Please correct the following errors:</p>
                <ul className="list-disc list-inside mt-2">
                  {Object.entries(errors).map(([key, value]) => (
                    <li key={key}>
                      {typeof value === 'string' ? value : Array.isArray(value) ? value[0] : value}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Name Field */}
            <div className="flex flex-col space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                name="bodygraph[name]"
                type="text"
                value={data.bodygraph.name}
                onChange={(e) => setData('bodygraph.name', e.target.value)}
                placeholder="Enter your full name"
                required
                className={errors.name ? 'border-destructive' : ''}
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </div>

            {/* Email Field */}
            <div className="flex flex-col space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="bodygraph[email]"
                type="email"
                value={data.bodygraph.email}
                onChange={(e) => {
                  setData('bodygraph.email', e.target.value);
                  if (emailError) setEmailError('');
                }}
                placeholder="Enter your email address"
                required
                className={(errors.email || emailError) ? 'border-destructive' : ''}
              />
              {(errors.email || emailError) && (
                <p className="text-sm text-destructive">{errors.email || emailError}</p>
              )}
            </div>

            {/* User Intent Field */}
            <div className="flex flex-col space-y-2">
              <Label htmlFor="user_intent">Intent</Label>
              <Textarea
                id="user_intent"
                name="bodygraph[user_intent]"
                value={data.bodygraph.user_intent}
                onChange={(e) => setData('bodygraph.user_intent', e.target.value)}
                placeholder="What is your intention for creating this profile?"
                required
                className={errors.user_intent ? 'border-destructive' : ''}
              />
              {errors.user_intent && (
                <p className="text-sm text-destructive">{errors.user_intent}</p>
              )}
            </div>

            {/* Birth Date and Time */}
            <div className="flex flex-col md:flex-row space-y-6 md:space-y-0 md:space-x-4">
              <div className="flex flex-col space-y-2 md:w-1/2">
                <Label htmlFor="birth_date">Birth Date</Label>
                <Input
                  id="birth_date"
                  name="bodygraph[birth_date]"
                  type="date"
                  value={data.bodygraph.birth_date}
                  onChange={(e) => setData('bodygraph.birth_date', e.target.value)}
                  required
                  className={errors.birth_date ? 'border-destructive' : ''}
                />
                {errors.birth_date && (
                  <p className="text-sm text-destructive">{errors.birth_date}</p>
                )}
              </div>

              <div className="flex flex-col space-y-2 md:w-1/2">
                <Label htmlFor="birth_time">Birth Time</Label>
                <Input
                  id="birth_time"
                  name="bodygraph[birth_time]"
                  type="time"
                  value={data.bodygraph.birth_time}
                  onChange={(e) => setData('bodygraph.birth_time', e.target.value)}
                  required
                  className={errors.birth_time ? 'border-destructive' : ''}
                />
                {errors.birth_time && (
                  <p className="text-sm text-destructive">{errors.birth_time}</p>
                )}
              </div>
            </div>

            {/* Birth City with Places Autocomplete */}
            <div className="flex flex-col space-y-2">
              <PlacesAutocomplete
                value={data.bodygraph.birth_city}
                onChange={(value) => setData('bodygraph.birth_city', value)}
                onPlaceSelect={handlePlaceSelect}
                placeholder="Enter birth city"
                label="Birth City"
                error={errors.birth_city}
                required
              />
            </div>

            {/* Birth Country */}
            <div className="flex flex-col space-y-2">
              <Label htmlFor="birth_country">Birth Country</Label>
              <Input
                id="birth_country"
                name="bodygraph[birth_country]"
                type="text"
                value={data.bodygraph.birth_country}
                onChange={(e) => setData('bodygraph.birth_country', e.target.value)}
                placeholder="Enter birth country"
                required
                className={errors.birth_country ? 'border-destructive' : ''}
              />
              {errors.birth_country && (
                <p className="text-sm text-destructive">{errors.birth_country}</p>
              )}
            </div>

            {/* Hidden fields for coordinates */}
            <input type="hidden" name="bodygraph[latitude]" value={data.bodygraph.latitude} />
            <input type="hidden" name="bodygraph[longitude]" value={data.bodygraph.longitude} />

            {/* Submit Button */}
            <div className="pt-4">
              <Button
                type="submit"
                className="w-full"
                disabled={processing}
              >
                {processing ? 'Creating...' : 'Complete Profile'}
              </Button>
            </div>
          </form>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-xs sm:text-sm text-muted-foreground">
          <p>This is a one-time invite link. Your profile will be created securely.</p>
        </div>
      </div>
    </div>
  );
};

export default InviteForm;
